/** Choose who a piece of work belongs to.
 *
 * Replaces a free-text box, which looked harmless and was not: a typed name is a string
 * and nothing else, so it links to no user record. The workload view, the team rollup and
 * "who is overloaded" all key on `assignee_user_id`, which the text box never set — so
 * every assignment made in the Workbench was invisible to every view built to answer
 * "who is doing what". "B. Landers", "Brian Landers" and "brian landers" were also three
 * different people as far as any grouping was concerned.
 *
 * Two behaviours worth keeping if this is ever rewritten:
 *
 * * **An existing free-text name is preserved, not silently dropped.** Items assigned
 *   before this picker existed carry a name and no id. Offering only the directory would
 *   mean opening a dialog and saving an unrelated field quietly cleared the assignee. So
 *   the existing name stays in the list, marked, until somebody deliberately re-picks.
 *   Same reasoning as the sprint picker keeping a completed sprint listed.
 * * **A deactivated assignee keeps their name.** `/people` only returns active people, so
 *   an item assigned to somebody who has since left would otherwise show as unassigned.
 *   The same branch covers it: the record of who did the work survives.
 */

import { SearchSelect, type SearchOption } from '@/components/SearchSelect'
import { Field } from '@/components/ui'
import { useSession } from '@/lib/session'
import { type AssigneeValue } from '@/features/people/assignee'
import { type Person, usePeople } from '@/features/people/usePeople'

/** Keyed by a sentinel rather than an id, because an unlinked name has none. */
const UNLISTED = '__unlisted__'

export function AssigneePicker({
  value,
  onChange,
  hint,
}: {
  value: AssigneeValue
  onChange: (next: AssigneeValue) => void
  hint?: string
}) {
  const { data: people, isPending, isError } = usePeople()
  const directory = people ?? []
  const { data: session } = useSession()

  // A name with no id, or an id the directory no longer returns: either way the person on
  // the item is not selectable from the list, and must not vanish because of that.
  const isUnlisted =
    value.name.trim() !== '' && !directory.some((person) => person.id === value.userId)

  const select = (id: string) => {
    if (id === '') {
      onChange({ userId: '', name: '' })
      return
    }
    if (id === UNLISTED) {
      onChange(value)
      return
    }
    const person = directory.find((candidate) => candidate.id === id)
    // Both fields move together. `assignee_name` is a denormalised copy that the board and
    // the share pages read without joining, so leaving it behind would put one name on the
    // card and a different person in the workload view.
    //
    // The stored name is the person's name — not `optionLabel`, whose "(external)" suffix
    // is a thing the picker says, not part of anybody's name.
    onChange({ userId: id, name: person?.displayName ?? '' })
  }

  return (
    <Field
      label="Assignee"
      hint={
        isError
          ? 'The people directory could not be read — try reopening this dialog.'
          : (hint ?? 'Nobody is a valid answer; a half-remembered name is not.')
      }
    >
      <SearchSelect
        value={isUnlisted ? UNLISTED : value.userId}
        onChange={select}
        disabled={isPending}
        emptyLabel="Unassigned"
        options={[
          ...(isUnlisted
            ? [
                {
                  value: UNLISTED,
                  // Only claimed once the directory has actually been read. While the
                  // query is in flight the name is shown plainly rather than accused of
                  // being stale.
                  label: `${value.name}${isPending || isError ? '' : ' — not in the directory'}`,
                },
              ]
            : []),
          // Yourself first: picking up a piece of work is the commonest assignment.
          ...[...directory]
            .sort((a, b) => Number(b.id === session?.userId) - Number(a.id === session?.userId))
            .map(
              (person): SearchOption => ({
                value: person.id,
                label: optionLabel(person),
                hint: person.id === session?.userId ? 'you' : person.email,
              }),
            ),
        ]}
      />
    </Field>
  )
}

function optionLabel(person: Person): string {
  return person.isExternal ? `${person.displayName} (external)` : person.displayName
}
